/**
 * Note Top Bar Transcript Component
 *
 * Top bar for transcript view mode
 * - Left section: "View Note" button to return to note view
 * - Right section: Empty (could add transcript-specific actions later)
 * - Menu: Delete Note
 *
 * Used when user switches from note view to transcript view
 */

import React, { useCallback, useMemo, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { useDeleteTranscriptsMutation } from '@features/encounters';
import { notesApi } from '@features/notes';
import {
  Article as ArticleIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig/hooks/useEventTracking';
import type { EncounterResponse } from '@store/generated/encounters';
import { useAppDispatch } from '@store/hooks';
import { useSnackbar } from 'notistack';

import type { MenuItemConfig } from './layouts/TopBarLayout';
import { TopBarLayout } from './layouts/TopBarLayout';

export interface NoteTopBarTranscriptProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Encounter ID for analytics
   */
  encounterId?: string;

  /**
   * Handler for switching back to note view
   */
  onViewNote: () => void;

  /**
   * Handler for deleting the note
   */
  onDeleteNote?: () => Promise<void>;

  /**
   * Encounter data for delete transcripts functionality
   */
  encounterData?: EncounterResponse;
}

/**
 * Top bar for transcript view
 * Provides navigation back to note view and standard actions
 */
export const NoteTopBarTranscript: React.FC<NoteTopBarTranscriptProps> = ({
  noteId,
  encounterId,
  onViewNote,
  onDeleteNote,
  encounterData,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { note, encounter: encounterTracking } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();

  // Delete transcripts state
  const [deleteTranscriptsDialogOpen, setDeleteTranscriptsDialogOpen] = useState(false);
  const [isDeletingTranscripts, setIsDeletingTranscripts] = useState(false);
  const [deleteTranscripts] = useDeleteTranscriptsMutation();

  const handleDeleteClick = () => {
    note.topbarDeleteClicked(noteId, {
      view_mode: 'transcript',
      is_signed: false,
      encounter_id: encounterId,
    });
    setDeleteDialogOpen(true);
  };

  const handleMenuOpen = () => {
    note.topbarMenuOpened(noteId, {
      view_mode: 'transcript',
      encounter_id: encounterId,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteNote) return;

    setIsDeleting(true);
    try {
      await onDeleteNote();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete note:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Delete transcripts handlers
  const handleDeleteTranscriptsClick = useCallback(() => {
    if (!encounterId) return;
    encounterTracking.deleteAllTranscriptsClicked({
      encounter_id: encounterId,
      dictation_count: encounterData?.inputs?.length ?? 0,
    });
    setDeleteTranscriptsDialogOpen(true);
  }, [encounterId, encounterTracking, encounterData?.inputs?.length]);

  const handleConfirmDeleteTranscripts = useCallback(async () => {
    if (!encounterId) return;
    encounterTracking.deleteAllTranscriptsConfirmed({
      encounter_id: encounterId,
      dictation_count: encounterData?.inputs?.length ?? 0,
    });
    setIsDeletingTranscripts(true);
    try {
      const result = await deleteTranscripts({ encounterId }).unwrap();
      encounterTracking.deleteAllTranscriptsCompleted({
        encounter_id: encounterId,
        transcription_sessions_deleted: result.transcription_sessions_deleted ?? 0,
        notes_cleared: result.notes_cleared ?? 0,
      });
      // Invalidate the current note cache to update can_regenerate
      if (noteId) {
        dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
      }
      enqueueSnackbar('Transcripts deleted successfully', { variant: 'success' });
      setDeleteTranscriptsDialogOpen(false);
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete transcripts';
      encounterTracking.deleteAllTranscriptsFailed({
        encounter_id: encounterId,
        error_message: errorMessage,
      });
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsDeletingTranscripts(false);
    }
  }, [encounterId, encounterTracking, encounterData?.inputs?.length, deleteTranscripts, enqueueSnackbar, noteId, dispatch]);

  const handleCancelDeleteTranscripts = useCallback(() => {
    if (encounterId) {
      encounterTracking.deleteAllTranscriptsCancelled({ encounter_id: encounterId });
    }
    setDeleteTranscriptsDialogOpen(false);
  }, [encounterId, encounterTracking]);

  // Build menu items configuration
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        label: 'Delete Note',
        icon: <DeleteIcon fontSize="small" />,
        onClick: handleDeleteClick,
        disabled: !onDeleteNote,
        divider: encounterData?.can_delete_transcripts === true && encounterData?.transcripts_deleted !== true,
      },
    ];

    // Add Delete Transcripts option when available (and not already deleted)
    if (encounterData?.can_delete_transcripts === true && encounterData?.transcripts_deleted !== true) {
      const transcriptCount = encounterData?.inputs?.length ?? 0;
      items.push({
        label: transcriptCount === 1 ? 'Delete Transcript' : 'Delete Transcripts',
        icon: <DeleteForeverIcon fontSize="small" color="error" />,
        onClick: handleDeleteTranscriptsClick,
        divider: true,
      });
    }

    return items;
  }, [onDeleteNote, encounterData?.can_delete_transcripts, encounterData?.transcripts_deleted, encounterData?.inputs?.length, handleDeleteTranscriptsClick]);

  // Left section: Empty for now (could add transcript-specific actions later)
  const leftSection = undefined;

  // Right section: With "View Note" button
  const rightSection = (
    <Button
      variant="contained"
      onClick={onViewNote}
      startIcon={<ArticleIcon />}
      sx={{
        textTransform: 'none',
        fontWeight: 500,
      }}
    >
      View Note
    </Button>
  );

  return (
    <>
      <TopBarLayout
        left={leftSection}
        right={rightSection}
        menuItems={menuItems}
        onMenuOpen={handleMenuOpen}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        variant="warning"
        title="Delete Note?"
        message="This action cannot be undone. Are you sure you want to delete this note?"
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />

      {/* Delete Transcripts Confirmation Dialog */}
      <ConfirmDialog
        open={deleteTranscriptsDialogOpen}
        variant="warning"
        title={`Delete ${(encounterData?.inputs?.length ?? 0) === 1 ? 'Transcript' : 'Transcripts'}?`}
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              This will permanently delete {(encounterData?.inputs?.length ?? 0) === 1 ? 'the transcript' : 'all transcripts'} from this encounter.
            </Typography>
            <Typography variant="body2" color="error" fontWeight={500}>
              This cannot be undone. Notes will no longer be able to be regenerated.
            </Typography>
          </Box>
        }
        onClose={handleCancelDeleteTranscripts}
        onConfirm={handleConfirmDeleteTranscripts}
        confirmText={`Delete ${(encounterData?.inputs?.length ?? 0) === 1 ? 'Transcript' : 'Transcripts'}`}
        cancelText="Cancel"
        loading={isDeletingTranscripts}
      />
    </>
  );
};
